import { useMemo, useState } from 'react'
import { kgToGrams } from '@/offline'
import { mainNetSince } from '../../model/day'
import { lotsWithFigures } from '../../model/delivery'
import { seasonLight, type SeasonLight } from '../../model/seasonLight'
import { useWizard, type WizardCtx } from './context'

export function useSeasonLight(ctx: WizardCtx): SeasonLight | null {
  const { d, roster, outbox } = ctx
  return useMemo(() => {
    if (!d.crop_id) return null
    const farmer = roster.farmers.find((f) => f.user_id === d.farmer.id)
    const season = farmer?.season.find((s) => s.crop_id === d.crop_id)
    const thisG = lotsWithFigures(d, {})
      .filter((l) => l.plan.stream === 'MAIN')
      .reduce((s, l) => s + l.figures.net_g, 0)
    return seasonLight({
      seasonToDateG: kgToGrams(season?.kg_to_date),
      ceilingG: kgToGrams(season?.ceiling_kg),
      todayOnPhoneG: mainNetSince(outbox, d.farmer.id, d.crop_id, roster.downloadedAt ?? 0),
      thisDeliveryG: thisG,
    })
  }, [d, roster, outbox])
}

/** The four weight lines are separate even when equal; money only as kg × rate. */
export function useLotFigures() {
  const { d, cards } = useWizard()
  return useMemo(
    () =>
      lotsWithFigures(d, {
        MAIN:
          cards.MAIN?.price_paise_per_kg != null
            ? { id: cards.MAIN.id, price_paise_per_kg: cards.MAIN.price_paise_per_kg }
            : undefined,
        CONV:
          cards.CONV?.price_paise_per_kg != null
            ? { id: cards.CONV.id, price_paise_per_kg: cards.CONV.price_paise_per_kg }
            : undefined,
      }),
    [d, cards],
  )
}

/**
 * A bag weight as the scale shows it: up to 2 decimals, parsed to whole grams. Outside
 * weight_min..weight_max it must be typed twice and match (F4.6). Calls `onReady` with the grams
 * and whether it was confirmed twice, or null while not ready.
 */
export function useWeightEntry(minG: number, maxG: number) {
  const [value, setValue] = useState<number | null>(null)
  const [first, setFirst] = useState<number | null>(null)
  const twoDecimals = value === null || value % 10 === 0
  const outOfRange = value !== null && (value < minG || value > maxG)
  const confirmedTwice = outOfRange && first !== null && first === value
  const ready = value !== null && value > 0 && twoDecimals && (!outOfRange || confirmedTwice)
  return {
    value,
    setValue,
    first,
    outOfRange,
    twoDecimals,
    confirmedTwice,
    ready,
    /** Press of Save while out of range and not yet confirmed: keep the first entry, ask again. */
    askAgain(): boolean {
      if (outOfRange && !confirmedTwice && value !== null) {
        setFirst(value)
        setValue(null)
        return true
      }
      return false
    },
    reset() {
      setValue(null)
      setFirst(null)
    },
  }
}
